// The <i3-chat-widget> panel is a fixed overlay. In its "sidebar" view mode it docks to one edge
// of the viewport and covers the portal, so mirror the panel width as an inset on the app root to
// squeeze the page instead. The widget reports its own state via `i3-widget-statechange`.

type AiChatWidgetSide = 'left' | 'right';

type AiChatWidgetStateChangeDetail = {
  open: boolean;
  viewMode: string;
  width: number;
  side: AiChatWidgetSide;
};

const STATE_CHANGE_EVENT = 'i3-widget-statechange';
const DOCKED_VIEW_MODE = 'sidebar';
const PANEL_ELEMENT_ID = 'chat-panel';
const STYLE_ELEMENT_ID = 'odp-ai-chat-squeeze-styles';
const DOCK_ATTRIBUTE = 'data-odp-ai-chat-dock';
const DOCK_WIDTH_VARIABLE = '--odp-ai-chat-dock-width';
// Below this the remaining page would be unusable, so let the panel overlay instead of squeezing.
const MIN_CONTENT_WIDTH_PX = 720;

const SQUEEZE_STYLES = `
#root {
  transition: margin 160ms ease-out, width 160ms ease-out;
}
body[${DOCK_ATTRIBUTE}] #root {
  width: calc(100% - var(${DOCK_WIDTH_VARIABLE}, 0px));
  box-sizing: border-box;
}
body[${DOCK_ATTRIBUTE}='right'] #root {
  margin-right: var(${DOCK_WIDTH_VARIABLE}, 0px);
}
body[${DOCK_ATTRIBUTE}='left'] #root {
  margin-left: var(${DOCK_WIDTH_VARIABLE}, 0px);
}
`;

// Kept outside React so the squeeze survives re-renders and route changes.
let lastState: AiChatWidgetStateChangeDetail | null = null;
let widgetElement: HTMLElement | null = null;

// The widget reports width via getBoundingClientRect(), which includes the scale(0.85) of its
// entrance animation, so a reopen under-reports by ~15%. offsetWidth ignores transforms.
function measureDockedWidth(state: AiChatWidgetStateChangeDetail): number {
  const panel = widgetElement?.shadowRoot?.getElementById(PANEL_ELEMENT_ID);
  return panel?.offsetWidth || state.width;
}

function ensureStyleElement(): void {
  if (document.getElementById(STYLE_ELEMENT_ID)) {
    return;
  }
  const style = document.createElement('style');
  style.id = STYLE_ELEMENT_ID;
  style.textContent = SQUEEZE_STYLES;
  document.head.appendChild(style);
}

function clearSqueeze(): void {
  document.body.removeAttribute(DOCK_ATTRIBUTE);
  document.body.style.removeProperty(DOCK_WIDTH_VARIABLE);
}

function applySqueeze(state: AiChatWidgetStateChangeDetail | null): void {
  if (!state || !state.open || state.viewMode !== DOCKED_VIEW_MODE) {
    clearSqueeze();
    return;
  }

  const width = measureDockedWidth(state);
  if (width <= 0 || window.innerWidth - width < MIN_CONTENT_WIDTH_PX) {
    clearSqueeze();
    return;
  }

  ensureStyleElement();
  document.body.style.setProperty(DOCK_WIDTH_VARIABLE, `${width}px`);
  document.body.setAttribute(DOCK_ATTRIBUTE, state.side);
}

// Re-asserts the inset after navigation, in case a page swapped the layout out from under it.
export function reapplyAiChatWidgetSqueeze(): void {
  applySqueeze(lastState);
}

export function installAiChatWidgetSqueeze(widget: HTMLElement): () => void {
  ensureStyleElement();
  widgetElement = widget;

  const onStateChange = (event: Event) => {
    const detail = (event as CustomEvent<AiChatWidgetStateChangeDetail>).detail;
    if (!detail) {
      return;
    }
    lastState = detail;
    applySqueeze(detail);
  };
  const onResize = () => applySqueeze(lastState);

  widget.addEventListener(STATE_CHANGE_EVENT, onStateChange);
  window.addEventListener('resize', onResize);
  applySqueeze(lastState);

  return () => {
    widget.removeEventListener(STATE_CHANGE_EVENT, onStateChange);
    window.removeEventListener('resize', onResize);
    lastState = null;
    widgetElement = null;
    clearSqueeze();
  };
}
